import { asNullableRecord } from "@openclaw/normalization-core/record-coerce";
import { normalizeUniqueTrimmedStringList } from "@openclaw/normalization-core/string-normalization";
import { formatUiExternalText } from "../lib/format-error.ts";
import { fetchControlUiResource } from "./browser-http.ts";

/** Decode a Gateway JSON response once, preserving validation details and HTTP status. */
export async function readControlUiJsonResponse(response: Response, signal: AbortSignal) {
  let data: Record<string, unknown> | null = null;
  try {
    data = asNullableRecord(await response.json());
  } catch {
    signal.throwIfAborted();
  }
  const error = data?.error;
  const message =
    typeof error === "string"
      ? error
      : error &&
          typeof error === "object" &&
          "message" in error &&
          typeof error.message === "string"
        ? error.message
        : "";
  const detail = formatUiExternalText(message);
  return {
    data,
    response,
    errorMessage: detail ? `HTTP ${response.status}: ${detail}` : `HTTP ${response.status}`,
  };
}

export type ControlUiAuthSource = {
  hello?: { auth?: { deviceToken?: string | null } | null } | null;
  settings?: { token?: string | null } | null;
  password?: string | null;
};

// Saved tokens and passwords are Bearer credentials too. Keep them after the
// live device token so callers can recover from a rejected credential.
export function resolveControlUiAuthCandidates(source: ControlUiAuthSource): string[] {
  return normalizeUniqueTrimmedStringList([
    source.hello?.auth?.deviceToken,
    source.settings?.token,
    source.password,
  ]).filter((token) => !/[\r\n]/.test(token));
}

export function resolveControlUiAuthToken(source: ControlUiAuthSource): string | null {
  return resolveControlUiAuthCandidates(source)[0] ?? null;
}

export function resolveControlUiAvatarAuth(source: ControlUiAuthSource) {
  return {
    authTokens: resolveControlUiAuthCandidates(source),
    // A completed hello admits avatar reads even without a Bearer token.
    authReady: Boolean(source.hello || source.settings?.token?.trim() || source.password?.trim()),
  };
}

export async function fetchWithControlUiAuth(
  url: string,
  init: Omit<RequestInit, "headers" | "signal"> & {
    headers?: Record<string, string>;
    signal: AbortSignal;
  },
  authCandidates: readonly string[],
  isCurrent: () => boolean,
): Promise<Response> {
  const candidates = authCandidates.length ? authCandidates : [""];
  const readOnly = !init.method || init.method === "GET" || init.method === "HEAD";
  for (let index = 0; ; index++) {
    init.signal.throwIfAborted();
    if (!isCurrent()) {
      throw new DOMException("Gateway request is no longer current", "AbortError");
    }
    const token = candidates[index];
    const response = await fetchControlUiResource(url, {
      ...init,
      ...(token ? { headers: { ...init.headers, Authorization: `Bearer ${token}` } } : {}),
    });
    init.signal.throwIfAborted();
    // A mutation's 403 is a scope/origin rejection, not a rejected credential.
    if (
      index === candidates.length - 1 ||
      (response.status !== 401 && !(readOnly && response.status === 403))
    ) {
      return response;
    }
    void response.body?.cancel().catch(() => undefined);
  }
}
